import React, { useState } from 'react';
import styled from 'styled-components';

const ContactSection = styled.section`
  padding: ${({ theme }) => `${theme.spacing['2xl']} 0`};
  background-color: ${({ theme }) => theme.colors.white};
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div`
  h2 {
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: 1.125rem;
    margin-bottom: 2rem;
  }
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;



const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  
  &:last-child {
    border-bottom: none;
  }
  
  .icon {
    width: 60px;
    height: 60px;
    background: ${({ theme }) => theme.colors.black};
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    flex-shrink: 0;
    transition: ${({ theme }) => theme.transitions.default};
    
    &:hover {
      background: ${({ theme }) => theme.colors.accent};
      transform: scale(1.1);
    }
  }
  
  .info {
    flex: 1;
    
    h4 {
      margin: 0 0 0.5rem 0;
      color: ${({ theme }) => theme.colors.black};
      font-weight: 600;
      font-size: 1.125rem;
    }
    
    p {
      margin: 0;
      color: ${({ theme }) => theme.colors.gray};
      line-height: 1.6;
    }
  }
`;

const ContactForm = styled.form`
  background: white;
  padding: 3rem;
  border-radius: 0;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.black};
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 0.875rem;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid ${({ theme }) => theme.colors.black};
  border-radius: 0;
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.default};
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const SubmitButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  width: 100%;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background-color: #10B981;
  color: white;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 1rem;
  text-align: center;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialLink = styled.a`
  width: 45px;
  height: 45px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

async function sendEmail() {
  try {
    const response = await fetch('/sendEmail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        from: 'onboarding@resend.dev',
        to: 'cdszczesny@gmail.com',
        subject: 'Hello World REACT',
        html: '<p>Congrats on sending your <strong>first email</strong>!</p>'
      })
    });

    const data = await response.json();
    console.log('Email sent:', data);
  } catch (err) {
    console.error('Error sending email:', err);
  }
}

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    sendEmail();
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 1000);
  };

  return (
    <ContactSection id="contact" className="section">
      <div className="container">
        <ContactContent>
          <ContactInfo>
            <h2>Skontaktuj się z nami</h2>
            <p>
              Chcesz rozpocząć przygodę z tańcem? Dowiedzieć się więcej o naszych zajęciach? 
              Skontaktuj się z nami - chętnie pomżemy Ci wybrać odpowiedni styl taneczny!
            </p>
            
            <ContactDetails>
              <ContactItem>
                <div className="icon">📍</div>
                <div className="info">
                  <h4>Adres</h4>
                  <p>ul. Przykładowa 123<br />65-001 Zielona Góra</p>
                </div>
              </ContactItem>
              
              <ContactItem>
                <div className="icon">📞</div>
                <div className="info">
                  <h4>Telefon</h4>
                  <p>+48 123 456 789</p>
                </div>
              </ContactItem>
              
              <ContactItem>
                <div className="icon">✉️</div>
                <div className="info">
                  <h4>Email</h4>
                  <p>kontakt@adaradance.pl</p>
                </div>
              </ContactItem>
              
              <ContactItem>
                <div className="icon">🕒</div>
                <div className="info">
                  <h4>Godziny pracy</h4>
                  <p>Pon - Pt: 9:00 - 18:00<br />Sob: 10:00 - 14:00</p>
                </div>
              </ContactItem>
            </ContactDetails>
            
            <SocialLinks>
              <SocialLink href="#" aria-label="Facebook">
                📘
              </SocialLink>
              <SocialLink href="#" aria-label="Instagram">
                📷
              </SocialLink>
              <SocialLink href="#" aria-label="LinkedIn">
                💼
              </SocialLink>
              <SocialLink href="#" aria-label="Twitter">
                🐦
              </SocialLink>
            </SocialLinks>
          </ContactInfo>
          
          <ContactForm onSubmit={handleSubmit}>
            {isSubmitted && (
              <SuccessMessage>
                Dziękujemy za wiadomość! Skontaktujemy się z Tobą wkrótce.
              </SuccessMessage>
            )}
            
            <FormGroup>
              <label htmlFor="name">Imię i nazwisko *</label>
              <FormInput
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <label htmlFor="email">Adres email *</label>
              <FormInput
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <label htmlFor="subject">Temat</label>
              <FormInput
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormGroup>
              <label htmlFor="message">Wiadomość *</label>
              <FormTextarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Opisz swoje zainteresowania taneczne lub zadaj pytanie..."
                required
              />
            </FormGroup>
            
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Wysyłanie...' : 'Zapisz się na zajęcia'}
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </div>
    </ContactSection>
  );
};

export default Contact;
